import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { SortBy, SortDirection } from 'constants/misc'
import { BrokerDTO, PageObject, PageRealEstatePropertyListItemDTO, PropertyAddressDTO, PropertyReorderContextDTO, PropertyType, PropertyWorkspaceInfoDTO, RealEstatePropertyAssignmentDTO, RealEstatePropertyDTO, RealEstatePropertyListItemDTO, RealEstatePropertyOrderStatusDTO, PropertySurfaceDTO } from 'models/property'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { PropertyEndpoint, RoleMimetype } from 'constants/API'
import { ProductSegment } from 'constants/product/productTypes'
import { DealDTOClient } from 'models/deal'
import { Nullable } from 'models/helpers'
import { PageableResponse } from 'models/misc'
import moment from 'moment'
import { useCallback, useMemo } from 'react'
import { useAPI } from 'utils/API'
import { logger } from 'utils/logging'
import { useDebouncedMemo } from 'utils/helpers/debouncedUseMemo'

const SEARCH_DEBOUNCE_MS = 250

/**
 * Opt-in representation override for the workspace-scoped property endpoints.
 *
 * `useAPI` derives `Accept` from the ACTING user's role
 * (`API.decideRoleMimeType`), but `/workspace/{ws}/realty*` serves the CLIENT
 * representation. An admin ordering on behalf of a client therefore sends the
 * admin mimetype and the BE answers **406 Not Acceptable**
 * (`Acceptable representations: [application/co.backbone.api.client.v1+json]`)
 * — reproduced live on dev for the text search, where replaying the same
 * request with the admin's own token + the client `Accept` returned 200. The
 * OpenAPI doc lists `client.v1` for the create endpoint too.
 *
 * Callers that read/write a CLIENT's property while acting as somebody else
 * (the PF v2 admin-on-behalf entry flow) opt in with `{ asClient: true }`.
 * Omitted, the request is byte-identical to before, so every other caller —
 * incl. the creative-reachable ChatWidget property step — is untouched.
 *
 * `Content-Type` is deliberately NOT overridden: create + patch consume plain
 * `application/json` (per the OpenAPI doc), which is what axios already sends.
 */
export interface PropertyRepresentationOptions {
  /** Pin `Accept` to the client representation instead of the acting role's. */
  asClient?: boolean
}

/**
 * Per-call axios config for `asClient` callers — intentionally returns an EMPTY
 * object when the override is off. Never emit `headers: undefined`: `useAPI`
 * spreads `options` OVER its own `{ headers: { Accept: role } }`, so an explicit
 * undefined would strip the role mimetype instead of leaving it alone.
 */
const _representationConfig = (options?: PropertyRepresentationOptions): AxiosRequestConfig =>
  options?.asClient ? { headers: { Accept: RoleMimetype.CLIENT } } : {}

/** Query-key fragment keeping the two representations in separate cache entries. */
const _representationKey = (options?: PropertyRepresentationOptions) =>
  options?.asClient ? { asClient: true } : undefined

// -- QUERIES
export function useGetPropertyDetail(propertyId: Nullable<string>, workspaceId: Nullable<string>, allowRetry = true, options?: PropertyRepresentationOptions) {
  const api = useAPI<PropertyEndpoint>()

  return useQuery<RealEstatePropertyDTO, AxiosError>({
    // `_representationKey` prevents an admin's own-role detail fetch (platform
    // property page) and the client-representation fetch (PF v2 entry) from
    // sharing one cache entry. Extra key props keep partial invalidations —
    // e.g. `[PROPERTY, GET_ONE, { propertyId, workspaceId }]` — matching.
    queryKey: [EntityKeys.PROPERTY, QueryType.GET_ONE, { propertyId: propertyId!, workspaceId: workspaceId!, ..._representationKey(options) }],
    queryFn: () => api.get<RealEstatePropertyDTO>(
      PropertyEndpoint.GET_PROPERTY_DETAIL,
      {
        workspaceId: workspaceId!,
        propertyId: propertyId!,
      },
      false,
      _representationConfig(options)
    ),
    enabled: !!propertyId && !!workspaceId,
    retry: allowRetry,
  })
}

interface PatchPropertyDetailPayload {
  title?: string,
  address?: PropertyAddressDTO | null,
  /** The closest routable point, when the address has none of its own. Sent
   *  separately from `address` so fixing routing never rewrites where the
   *  property is (`UpdateRealEstatePropertyDiffDTO.creativeRoutingAddress`). */
  creativeRoutingAddress?: PropertyAddressDTO | null,
  propertyType?: PropertyType | null,
  propertyUsage?: PropertyType | null,
  /**
   * Surface area. The platform DTO deprecates the flat `area`/`areaUnit` pair in
   * favour of this object, so only this form is sent. `value` must be strictly
   * positive — the backend annotates it `@Positive` and rejects 0.
   */
  surface?: PropertySurfaceDTO,
  /** The area band the property is priced in (backend-platform#881); an exact `surface` re-derives it. */
  areaRange?: ProductSegment,
}

export function usePatchPropertyDetail(options?: PropertyRepresentationOptions) {
  const api = useAPI<PropertyEndpoint>()

  return getMutation<AxiosResponse, { workspaceId: string, propertyId: string, payload: PatchPropertyDetailPayload }>(
    ({ workspaceId, propertyId, payload }) => api.patch(
      PropertyEndpoint.PATCH_PROPERTY_DETAIL,
      {
        workspaceId: workspaceId!,
        propertyId: propertyId!,
      },
      payload,
      true,
      _representationConfig(options)
    )
  )
}

export function useGetPropertyDetailWithObjectReferenceId(objectReferenceId: Nullable<string>, workspaceId: Nullable<string>, allowRetry = true) {
  const api = useAPI<PropertyEndpoint>()

  return useQuery<RealEstatePropertyDTO, AxiosError>({
    queryKey: [EntityKeys.PROPERTY, QueryType.GET_ONE, { objectReferenceId: objectReferenceId!, workspaceId: workspaceId! }],
    queryFn: () => api.get<RealEstatePropertyDTO>(
      PropertyEndpoint.GET_PROPERTY_DETAIL_WITH_OBJECT_REFERENCE,
      {
        workspaceId: workspaceId!,
        objectReferenceId: objectReferenceId!,
      },
      false
    ),
    enabled: !!objectReferenceId && !!workspaceId,
    retry: allowRetry,
  })
}

export interface PropertyListParams {
  searchText: string,
  brokerId?: string
  page?: number
  size?: number
  sortBy?: SortBy
  sortDirection?: SortDirection
  creationDateStart?: string
  creationDateEnd?: string
  orderStatus?: string
}

const defaultParams: PropertyListParams = {
  searchText: '',
  page: 0,
  size: 10,
}

const _serializeParams = (params: PropertyListParams) => {

  const finalParams = {
    ...defaultParams,
    ...params,
  }

  const serializedParams: any = {
    searchText: finalParams.searchText,
    brokerId: finalParams.brokerId,
    page: (finalParams.page || 0).toString(),
    size: (finalParams.size || 25).toString(),
    creationDateFrom: finalParams.creationDateStart ? moment.utc(finalParams.creationDateStart).toISOString() : null,
    creationDateTo: finalParams.creationDateEnd ? moment.utc(finalParams.creationDateEnd).endOf('day').toISOString() : null,
    orderStatus: finalParams.orderStatus,
  }

  // Map our SortBy enum to backend's sortProperty enum
  const mapSortByToBackend = (sortBy: SortBy): string => {
    switch (sortBy) {
      case SortBy.PROPERTY_TITLE:
        return 'TITLE'
      case SortBy.PROPERTY_CREATION_DATE:
        return 'CREATION_TIMESTAMP'
      case SortBy.LAST_UPDATED_DATE:
        return 'UPDATED_AT'
      default:
        return 'DEFAULT'
    }
  }

  // Map our SortDirection enum to backend's sortDirection enum
  const mapSortDirectionToBackend = (sortDirection: SortDirection): string => {
    switch (sortDirection) {
      case SortDirection.ASCENDING:
        return 'ASCENDING'
      case SortDirection.DESCENDING:
        return 'DESCENDING'
      default:
        return 'NONE'
    }
  }

  // Include sort parameters if they are provided
  if (finalParams.sortBy) {
    serializedParams.sortProperty = mapSortByToBackend(finalParams.sortBy)
  }

  if (finalParams.sortDirection) {
    serializedParams.sortDirection = mapSortDirectionToBackend(finalParams.sortDirection)
  }

  return serializedParams
}

export function useGetPropertyAssignments(propertyId: string, workspaceId: string) {
  const api = useAPI<PropertyEndpoint>()

  return useQuery({
    queryKey: [EntityKeys.PROPERTY_ASSIGNMENTS, QueryType.LIST, { propertyId, workspaceId }],
    queryFn: () => api.get<Array<RealEstatePropertyAssignmentDTO>>(
      PropertyEndpoint.GET_PROPERTY_ASSIGNMENTS,
      {
        propertyId,
        workspaceId
      },
      true
    ),
    enabled: !!propertyId && !!workspaceId,
    retry: true,
  })
}

export function useGetPropertyOrders(propertyId: string, workspaceId: string) {
  const api = useAPI<PropertyEndpoint>()

  return useQuery({
    queryKey: [EntityKeys.PROPERTY_ORDERS, QueryType.LIST, { propertyId, workspaceId }],
    queryFn: async () => {
      try {
        return await api.get<Array<DealDTOClient>>(
          PropertyEndpoint.GET_PROPERTY_ORDERS,
          {
            propertyId,
            workspaceId
          },
          true
        )
      } catch (error) {
        logger.error('Failed to fetch property orders', error as Error, {
          context: {
            component: 'useGetPropertyOrders',
            metadata: { propertyId, workspaceId },
          }
        })

        throw error
      }
    },
    enabled: !!propertyId && !!workspaceId,
    retry: true,
  })
}

export function useGetPropertyOrderStatuses(objectReferenceId: Nullable<string>, workspaceId: Nullable<string>) {
  const api = useAPI<PropertyEndpoint>()

  return useQuery<Array<RealEstatePropertyOrderStatusDTO>, AxiosError>({
    queryKey: [EntityKeys.PROPERTY_ORDERS, QueryType.LIST, { objectReferenceId: objectReferenceId!, workspaceId: workspaceId! }],
    queryFn: async () => {
      try {
        return await api.get<Array<RealEstatePropertyOrderStatusDTO>>(
          PropertyEndpoint.GET_PROPERTY_ORDER_STATUSES_WITH_OBJECT_REFERENCE,
          {
            workspaceId: workspaceId!,
            objectReferenceId: objectReferenceId!,
          },
          false
        )
      } catch (error) {
        logger.error('Failed to fetch property order statuses', error as Error, {
          context: {
            component: 'useGetPropertyOrderStatuses',
            metadata: { objectReferenceId, workspaceId },
          }
        })

        throw error
      }
    },
    enabled: !!objectReferenceId && !!workspaceId,
  })
}

export type PropertyReorderContextKey = { workspaceId: string, propertyId: string } | { workspaceId: string, objectReferenceId: string }

// Analytics only and read on every property view: one warning per endpoint and page load is enough for Sentry
const warnedReorderContextReads = new Set<PropertyEndpoint>()

const propertyReorderContextQuery = (api: ReturnType<typeof useAPI<PropertyEndpoint>>, key: PropertyReorderContextKey) => ({
  queryKey: [EntityKeys.PROPERTY_REORDER_CONTEXT, QueryType.GET_ONE, key],
  queryFn: async () => {
    const endpoint = 'propertyId' in key ? PropertyEndpoint.GET_PROPERTY_REORDER_CONTEXT : PropertyEndpoint.GET_PROPERTY_REORDER_CONTEXT_WITH_OBJECT_REFERENCE
    try {
      return await api.get<PropertyReorderContextDTO>(endpoint, key, false)
    } catch (error) {
      if (!warnedReorderContextReads.has(endpoint)) {
        warnedReorderContextReads.add(endpoint)
        logger.warn('Failed to fetch the property re-order context', error as Error, {
          context: {
            component: 'propertyReorderContextQuery',
            action: 'fetchPropertyReorderContext',
            metadata: key,
          }
        })
      }

      throw error
    }
  },
  retry: false,
})

export function useGetPropertyReorderContext(propertyId: Nullable<string>, workspaceId: Nullable<string>, enabled = true) {
  const api = useAPI<PropertyEndpoint>()

  return useQuery<PropertyReorderContextDTO, AxiosError>({
    ...propertyReorderContextQuery(api, { workspaceId: workspaceId!, propertyId: propertyId! }),
    enabled: enabled && !!propertyId && !!workspaceId,
  })
}

export function useGetPropertyReorderContextWithObjectReferenceId(objectReferenceId: Nullable<string>, workspaceId: Nullable<string>) {
  const api = useAPI<PropertyEndpoint>()

  return useQuery<PropertyReorderContextDTO, AxiosError>({
    ...propertyReorderContextQuery(api, { workspaceId: workspaceId!, objectReferenceId: objectReferenceId! }),
    enabled: !!objectReferenceId && !!workspaceId,
  })
}

/** Reads the context for the order click itself: a settled cached value at once, else the read (joining one in flight). */
export function usePropertyReorderContextResolver() {
  const api = useAPI<PropertyEndpoint>()
  const client = useQueryClient()

  return useCallback((key: PropertyReorderContextKey): PropertyReorderContextDTO | Promise<PropertyReorderContextDTO> => {
    const query = propertyReorderContextQuery(api, key)
    // An order invalidates the key, but an unwatched query keeps handing out the old context until its refetch lands
    const state = client.getQueryState<PropertyReorderContextDTO>(query.queryKey)
    if (state?.data !== undefined && !state.isInvalidated && state.fetchStatus === 'idle') return state.data
    return client.fetchQuery(query)
  }, [api, client])
}

export const useInfiniteListProperties = (workspaceId: string, params: PropertyListParams) => {
  const api = useAPI<PropertyEndpoint>()

  const serializedParams = useMemo(() => _serializeParams(params), [params])

  const fetchFnc = (page: number) => api.get<PageableResponse<RealEstatePropertyListItemDTO[]>>(
    PropertyEndpoint.GET_SEARCH_PROPERTY,
    { workspaceId },
    true,
    {
      params: {
        ...serializedParams,
        page,
      }
    }
  )

  return useInfiniteQuery({
    queryKey: [EntityKeys.PROPERTY, QueryType.INFINITE_LIST, workspaceId, { ...serializedParams }],
    queryFn: ({ pageParam }) => fetchFnc(pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => !lastPage.data.last ? lastPage.data.pageable.pageNumber + 1 : undefined,
    enabled: !!workspaceId,
  })
}

/** Searches the workspace's properties; the search text is debounced, and the last results stay shown while typing. */
export function useGetSearchProperty(workspaceId: string, params: PropertyListParams, options?: PropertyRepresentationOptions) {
  const api = useAPI<PropertyEndpoint>()

  const searchText = useDebouncedMemo(() => params.searchText, [params.searchText], SEARCH_DEBOUNCE_MS)
  const serializedParams = useMemo(() => _serializeParams({ ...params, searchText }), [params, searchText])

  return useQuery({
    queryKey: [EntityKeys.PROPERTY, QueryType.LIST, { workspaceId, searchText, ..._representationKey(options) }],
    queryFn: () => api.get<PageRealEstatePropertyListItemDTO>(
      PropertyEndpoint.GET_SEARCH_PROPERTY,
      { workspaceId },
      true,
      {
        params: {
          ...serializedParams
        },
        // Merged, not replaced — the query params above must survive.
        ..._representationConfig(options),
      }
    ),
    enabled: !!workspaceId,
    placeholderData: keepPreviousData,
  })
}

interface PropertyBrokersParams {
  query?: string
  page?: number
  size?: number
}

export function useGetPropertyBrokers(workspaceId: string, params?: PropertyBrokersParams) {
  const api = useAPI<PropertyEndpoint>()

  return useQuery({
    queryKey: [EntityKeys.PROPERTY, QueryType.GET_ONE, { workspaceId, ...params }],
    queryFn: () => api.get<PageObject<BrokerDTO>>(
      PropertyEndpoint.GET_PROPERTY_BROKERS,
      {
        workspaceId: workspaceId!,
      },
      true,
      {
        params
      }
    ),
    enabled: !!workspaceId,
  })
}

export function useGetPropertyWorkspace(propertyId: string) {
  const api = useAPI<PropertyEndpoint>()

  return useQuery({
    queryKey: [EntityKeys.PROPERTY, QueryType.GET_ONE, { propertyId }],
    queryFn: () => api.get<PropertyWorkspaceInfoDTO>(
      PropertyEndpoint.GET_PROPERTY_WORKSPACE,
      {
        propertyId,
      },
      true
    ),
    enabled: !!propertyId
  })
}

interface CreateNewPropertyPayload {
  workspaceId: string
  data: {
    title: string
    address: {
      street: string
      streetNumber: string
      postalCode: string
      city: string
      country: string
      coordinates: {
        lat: number
        lng: number
      }
    },
    /** The closest routable point when the address itself has none — kept apart
     *  from `address.coordinates` so a snap never overwrites where the property
     *  is. The BE accepts it on this payload and placement prefers it for
     *  creative routing. */
    creativeRoutingAddress?: {
      street: string
      streetNumber: string
      postalCode: string
      city: string
      country: string
      coordinates: {
        lat: number
        lng: number
      }
    },
    /** Exact surface area. Optional: an exact area sets the band it falls into, over `areaRange`. */
    surface?: PropertySurfaceDTO,
    /**
     * The area band the property is priced in (backend-platform#881). Session-create rejects a property with
     * neither this nor a usable `surface` (422), so the purchase flow always sends it.
     */
    areaRange?: ProductSegment,
    /** Optional: the backend accepts a property without one, and the new purchase flow does not ask for it. */
    propertyUsage?: PropertyType
    /** Internal user id (UUID) of the property's broker; an admin creating a property for a client names the client here. */
    brokerId?: string
  }
}

export function useCreateNewProperty(options?: PropertyRepresentationOptions) {
  const api = useAPI<PropertyEndpoint>()

  return getMutation<AxiosResponse<{ id: string }>, CreateNewPropertyPayload>(
    ({ data, workspaceId }) => api.post(
      PropertyEndpoint.POST_CREATE_NEW_PROPERTY,
      { workspaceId },
      data,
      true,
      _representationConfig(options)
    )
  )
}

interface MergePropertiesPayload {
  workspaceId: string
  targetPropertyId: string
  sourcePropertyIds: string[]
}

export function useMergeProperties() {
  const api = useAPI<PropertyEndpoint>()

  return getMutation<AxiosResponse<RealEstatePropertyDTO>, MergePropertiesPayload>(
    ({ workspaceId, targetPropertyId, sourcePropertyIds }) => api.post(
      PropertyEndpoint.POST_MERGE_PROPERTIES,
      {
        workspaceId,
        targetPropertyId,
      },
      { sourcePropertyIds },
      true
    )
  )
}

interface TransferPropertiesPayload {
  workspaceId: string
  realtyIds: string[]
  targetWorkspaceId: string
  newBrokerId?: string
  /** Acceptance of the data-transfer consent. The backend rejects the request with 400 unless it is true. */
  dataTransferConsentGiven: boolean
}

export function useTransferProperties() {
  const api = useAPI<PropertyEndpoint>()

  return getMutation<AxiosResponse, TransferPropertiesPayload>(
    ({ workspaceId, realtyIds, targetWorkspaceId, newBrokerId, dataTransferConsentGiven }) => api.post(
      PropertyEndpoint.POST_TRANSFER_PROPERTIES,
      { workspaceId },
      { realtyIds, targetWorkspaceId, newBrokerId, dataTransferConsentGiven },
      true
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.PROPERTY] })
    }
  )
}

interface BulkDeletePropertiesPayload {
  workspaceId: string
  realtyIds: string[]
}

export function useBulkDeleteProperties() {
  const api = useAPI<PropertyEndpoint>()

  return getMutation<AxiosResponse, BulkDeletePropertiesPayload>(
    ({ workspaceId, realtyIds }) => api.post(
      PropertyEndpoint.POST_BULK_DELETE_PROPERTIES,
      { workspaceId },
      { realtyIds },
      true
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.PROPERTY] })
    }
  )
}

interface ReassignBrokerPayload {
  workspaceId: string
  realtyIds: string[]
  newBrokerId: string
}

export function useReassignBroker() {
  const api = useAPI<PropertyEndpoint>()

  return getMutation<AxiosResponse, ReassignBrokerPayload>(
    ({ workspaceId, realtyIds, newBrokerId }) => api.post(
      PropertyEndpoint.POST_REASSIGN_BROKER,
      { workspaceId },
      { realtyIds, newBrokerId },
      true
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.PROPERTY] })
    }
  )
}
